import { Registry, type Identified } from './Registry';
import { materials, gearMaterials, type Rarity } from './materials';
import { fishSpecies } from './fish';
import { buildPieces } from './build';

/**
 * Merkezî item veri tabanı.
 * Ekipmanlar malzeme tanımlarından ÜRETİLİR: yeni bir maden eklendiğinde
 * kılıcı, kazması, baltası, küreği, çapası ve 4 zırh parçası kendiliğinden oluşur.
 */

export type ItemKind =
  | 'material'
  | 'tool'
  | 'weapon'
  | 'armor'
  | 'food'
  | 'placeable'
  | 'fish'
  | 'potion'
  | 'seed'
  | 'misc';

export type ToolClass = 'pickaxe' | 'axe' | 'shovel' | 'hoe' | 'sword' | 'bow' | 'hammer' | 'rod';
export type ArmorSlot = 'head' | 'chest' | 'legs' | 'feet';

export interface EffectSpec {
  id: string;
  /** Saniye. */
  duration: number;
  magnitude: number;
}

export interface ToolSpec {
  class: ToolClass;
  damage: number;
  speed: number;
  durability: number;
  harvestLevel: number;
  /** Yay için: menzil ve maksimum germe süresi. */
  range?: number;
  drawTime?: number;
}

export interface ArmorSpec {
  slot: ArmorSlot;
  defense: number;
  durability: number;
  /** Set bonusu kimliği (4 parça tamamlanınca aktifleşir). */
  set?: string;
}

export interface FoodSpec {
  hunger: number;
  health?: number;
  effects?: EffectSpec[];
}

export interface ItemDef extends Identified {
  name: string;
  icon: string;
  kind: ItemKind;
  /** Yığın limiti; araç/zırh 1. */
  stack: number;
  /** BEDO satış değeri (Kogox). 0 = satılamaz. */
  value: number;
  rarity: Rarity;
  material?: string;
  tool?: ToolSpec;
  armor?: ArmorSpec;
  food?: FoodSpec;
  potion?: { effects: EffectSpec[] };
  /** Yerleştirilebilir yapı/eşya kimliği. */
  place?: { piece: string };
  /** Kısa açıklama (UI). */
  desc?: string;
}

export const items = new Registry<ItemDef>('items');

const TOOL_LABEL: Record<Exclude<ToolClass, 'bow' | 'rod'>, string> = {
  pickaxe: 'Kazma',
  axe: 'Balta',
  shovel: 'Kürek',
  hoe: 'Çapa',
  sword: 'Kılıç',
  hammer: 'Çekiç',
};

const TOOL_ICON: Record<ToolClass, string> = {
  pickaxe: '⛏️',
  axe: '🪓',
  shovel: '🥄',
  hoe: '🌾',
  sword: '🗡️',
  hammer: '🔨',
  bow: '🏹',
  rod: '🎣',
};

const ARMOR_LABEL: Record<ArmorSlot, string> = {
  head: 'Kask',
  chest: 'Göğüslük',
  legs: 'Pantolon',
  feet: 'Bot',
};

const ARMOR_ICON: Record<ArmorSlot, string> = {
  head: '🪖',
  chest: '🦺',
  legs: '👖',
  feet: '🥾',
};

// ---------------------------------------------------------------- ham malzemeler
for (const m of materials.all()) {
  items.register({
    id: m.id,
    name: m.name,
    icon: m.icon,
    kind: 'material',
    stack: 999,
    value: m.value,
    rarity: m.rarity,
    material: m.id,
  });
}

// ---------------------------------------------------------------- türetilmiş ekipman
const TOOL_CLASSES: Array<Exclude<ToolClass, 'bow' | 'rod'>> = ['sword', 'pickaxe', 'axe', 'shovel', 'hoe', 'hammer'];
/** Araç sınıfına göre hasar/hız/dayanıklılık çarpanları. */
const TOOL_MOD: Record<Exclude<ToolClass, 'bow' | 'rod'>, { dmg: number; spd: number; dur: number }> = {
  sword: { dmg: 1, spd: 1, dur: 1 },
  pickaxe: { dmg: 0.55, spd: 1, dur: 1.1 },
  axe: { dmg: 0.8, spd: 0.9, dur: 1.05 },
  shovel: { dmg: 0.45, spd: 1.1, dur: 0.9 },
  hoe: { dmg: 0.4, spd: 1.2, dur: 0.8 },
  hammer: { dmg: 0.7, spd: 0.8, dur: 1.3 },
};

for (const m of gearMaterials()) {
  const g = m.gear!;
  for (const cls of TOOL_CLASSES) {
    const mod = TOOL_MOD[cls];
    items.register({
      id: `${m.id}_${cls}`,
      name: `${m.name} ${TOOL_LABEL[cls]}`,
      icon: TOOL_ICON[cls],
      kind: cls === 'sword' ? 'weapon' : 'tool',
      stack: 1,
      value: Math.round(m.value * 2.2),
      rarity: m.rarity,
      material: m.id,
      tool: {
        class: cls,
        damage: Math.max(1, Math.round(g.damage * mod.dmg)),
        speed: +(g.speed * mod.spd).toFixed(2),
        durability: Math.round(g.durability * mod.dur),
        harvestLevel: g.harvestLevel,
      },
    });
  }
  for (const slot of ['head', 'chest', 'legs', 'feet'] as ArmorSlot[]) {
    items.register({
      id: `${m.id}_${slot}`,
      name: `${m.name} ${ARMOR_LABEL[slot]}`,
      icon: ARMOR_ICON[slot],
      kind: 'armor',
      stack: 1,
      value: Math.round(m.value * 2),
      rarity: m.rarity,
      material: m.id,
      armor: {
        slot,
        defense: g.defense[slot],
        durability: Math.round(g.durability * 0.9),
        set: m.id,
      },
    });
  }
}

// ---------------------------------------------------------------- temel eşyalar
items.register(
  { id: 'stick', name: 'Çubuk', icon: '🪄', kind: 'material', stack: 999, value: 1, rarity: 'common' },
  { id: 'rope', name: 'İp', icon: '🪢', kind: 'material', stack: 999, value: 3, rarity: 'common' },
  { id: 'fiber', name: 'Lif', icon: '🌿', kind: 'material', stack: 999, value: 1, rarity: 'common' },
  { id: 'sand', name: 'Kum', icon: '🏖️', kind: 'material', stack: 999, value: 1, rarity: 'common' },
  { id: 'glass', name: 'Cam', icon: '🪟', kind: 'material', stack: 999, value: 6, rarity: 'common' },
  { id: 'sand_fine', name: 'İnce Kum', icon: '⏳', kind: 'material', stack: 999, value: 2, rarity: 'common', desc: 'Öğütücüden çıkar, cam için gerekli.' },
  { id: 'leather', name: 'Deri', icon: '🟤', kind: 'material', stack: 999, value: 6, rarity: 'common' },
  { id: 'feather', name: 'Tüy', icon: '🪶', kind: 'material', stack: 999, value: 2, rarity: 'common' },
  { id: 'arrow', name: 'Ok', icon: '➶', kind: 'material', stack: 256, value: 1, rarity: 'common' },
  { id: 'venom_sac', name: 'Zehir Kesesi', icon: '🧪', kind: 'material', stack: 99, value: 22, rarity: 'rare' },
  { id: 'glow_mushroom', name: 'Parlayan Mantar', icon: '🍄', kind: 'material', stack: 99, value: 9, rarity: 'uncommon' },
  { id: 'kogox_pouch', name: 'Kogox Kesesi', icon: '💰', kind: 'misc', stack: 99, value: 0, rarity: 'uncommon', desc: 'Açıldığında Kogox verir.' },
);

// ---------------------------------------------------------------- özel araçlar
items.register(
  {
    id: 'bow',
    name: 'Yay',
    icon: '🏹',
    kind: 'weapon',
    stack: 1,
    value: 45,
    rarity: 'uncommon',
    tool: { class: 'bow', damage: 12, speed: 1, durability: 240, harvestLevel: 0, range: 90, drawTime: 0.85 },
    desc: '4 Çubuk + 1 İp. Basılı tut → ger, bırak → at.',
  },
  {
    id: 'fishing_rod',
    name: 'Olta',
    icon: '🎣',
    kind: 'tool',
    stack: 1,
    value: 30,
    rarity: 'common',
    tool: { class: 'rod', damage: 1, speed: 1, durability: 180, harvestLevel: 0 },
  },
  {
    id: 'fishing_rod_pro',
    name: 'Gelişmiş Olta',
    icon: '🎣',
    kind: 'tool',
    stack: 1,
    value: 140,
    rarity: 'rare',
    tool: { class: 'rod', damage: 1, speed: 1.6, durability: 420, harvestLevel: 0 },
    desc: 'Nadir balık şansını artırır.',
  },
);

// ---------------------------------------------------------------- GÜÇ ZIRHI (özel set)
const POWER_STATS: Record<ArmorSlot, { def: number; dur: number }> = {
  head: { def: 9, dur: 1200 },
  chest: { def: 16, dur: 1500 },
  legs: { def: 13, dur: 1350 },
  feet: { def: 9, dur: 1200 },
};

for (const slot of ['head', 'chest', 'legs', 'feet'] as ArmorSlot[]) {
  items.register({
    id: `power_${slot}`,
    name: `Güç Zırhı ${ARMOR_LABEL[slot]}`,
    icon: '⚡',
    kind: 'armor',
    stack: 1,
    value: 900,
    rarity: 'legendary',
    armor: { slot, defense: POWER_STATS[slot].def, durability: POWER_STATS[slot].dur, set: 'power' },
    desc: '4 parça tamamlanınca ÖZ GÜÇ açılır.',
  });
}

// ---------------------------------------------------------------- KUG KILICI (Ares ödülü)
items.register({
  id: 'kug_sword',
  name: 'KUG Kılıcı',
  icon: '🗡️',
  kind: 'weapon',
  stack: 1,
  value: 0,
  rarity: 'legendary',
  tool: { class: 'sword', damage: 65, speed: 3.2, durability: 3000, harvestLevel: 7 },
  desc: 'Ares’in külünden dövülmüş. Üzerinden siyah dumanlar yükselir.',
});

// ---------------------------------------------------------------- yiyecek
items.register(
  { id: 'berry', name: 'Böğürtlen', icon: '🫐', kind: 'food', stack: 99, value: 2, rarity: 'common', food: { hunger: 6 } },
  { id: 'apple', name: 'Elma', icon: '🍎', kind: 'food', stack: 99, value: 4, rarity: 'common', food: { hunger: 12 } },
  { id: 'raw_meat', name: 'Çiğ Et', icon: '🥩', kind: 'food', stack: 99, value: 5, rarity: 'common', food: { hunger: 10, health: -1 } },
  { id: 'cooked_meat', name: 'Pişmiş Et', icon: '🍖', kind: 'food', stack: 99, value: 12, rarity: 'common', food: { hunger: 28, health: 2 } },
  { id: 'bread', name: 'Ekmek', icon: '🍞', kind: 'food', stack: 99, value: 10, rarity: 'common', food: { hunger: 22 } },
  { id: 'wheat', name: 'Buğday', icon: '🌾', kind: 'material', stack: 999, value: 3, rarity: 'common' },
  { id: 'cooked_fish', name: 'Pişmiş Balık', icon: '🐟', kind: 'food', stack: 99, value: 14, rarity: 'common', food: { hunger: 24, health: 1 } },
  { id: 'soup', name: 'Çorba', icon: '🍲', kind: 'food', stack: 20, value: 24, rarity: 'uncommon', food: { hunger: 40, health: 4 } },
);

// ---------------------------------------------------------------- tohumlar
items.register(
  { id: 'wheat_seed', name: 'Buğday Tohumu', icon: '🌱', kind: 'seed', stack: 999, value: 2, rarity: 'common', place: { piece: 'crop_wheat' } },
  { id: 'berry_seed', name: 'Böğürtlen Tohumu', icon: '🌱', kind: 'seed', stack: 999, value: 3, rarity: 'common', place: { piece: 'crop_berry' } },
);

// ---------------------------------------------------------------- iksirler
const potion = (id: string, name: string, icon: string, effects: EffectSpec[], value: number, rarity: Rarity): ItemDef => ({
  id,
  name,
  icon,
  kind: 'potion',
  stack: 20,
  value,
  rarity,
  potion: { effects },
  desc: `${Math.round(effects[0]!.duration / 60)} dakika etki.`,
});

// Geçici iksirler en az 3 dakika (180 s) etki eder.
items.register(
  potion('potion_speed', 'Hız İksiri', '🧴', [{ id: 'speed', duration: 180, magnitude: 1.35 }], 60, 'uncommon'),
  potion('potion_strength', 'Güç İksiri', '🧴', [{ id: 'strength', duration: 180, magnitude: 1.5 }], 80, 'uncommon'),
  potion('potion_nightvision', 'Gece Görüşü', '🧴', [{ id: 'nightvision', duration: 240, magnitude: 1 }], 70, 'uncommon'),
  potion('potion_mining', 'Madencilik Hızı', '🧴', [{ id: 'mining', duration: 240, magnitude: 1.6 }], 75, 'uncommon'),
  potion('potion_antivenom', 'Zehir Direnci', '🧴', [{ id: 'venomResist', duration: 300, magnitude: 1 }], 85, 'rare'),
  potion('potion_fishing', 'Balıkçı Şansı', '🧴', [{ id: 'fishLuck', duration: 300, magnitude: 1.8 }], 95, 'rare'),
  potion('potion_satiety', 'Tokluk İksiri', '🧴', [{ id: 'satiety', duration: 360, magnitude: 0.5 }], 65, 'uncommon'),
);

// ---------------------------------------------------------------- yerleştirilebilir yapı parçaları
// Yapı parçaları ve istasyonlar tek kaynaktan (build.ts) türetilir.
// Tarım ekimleri hariç tutulur: onlar tohum item'ıyla dikilir.
for (const piece of buildPieces.all()) {
  if (piece.shape === 'crop') continue;
  items.register({
    id: `place_${piece.id}`,
    name: piece.shape === 'station' ? piece.name : piece.name,
    icon: piece.icon,
    kind: 'placeable',
    stack: piece.shape === 'station' ? 9 : 99,
    value: Math.max(1, Math.round(piece.cost.reduce((s, c) => s + c.count, 0))),
    rarity: 'common',
    place: { piece: piece.id },
    desc: piece.desc,
  });
}

// ---------------------------------------------------------------- balıklar
for (const f of fishSpecies.all()) {
  items.register({
    id: f.id,
    name: f.name,
    icon: f.icon,
    kind: 'fish',
    stack: 99,
    value: f.value,
    rarity: f.rarity,
    food: { hunger: f.hunger },
    desc: f.desc,
  });
}

/** UI ve sistemler için güvenli erişim: tanım yoksa görünür bir yer tutucu döner. */
const FALLBACK: ItemDef = Object.freeze({
  id: 'unknown',
  name: 'Bilinmeyen',
  icon: '❔',
  kind: 'misc',
  stack: 1,
  value: 0,
  rarity: 'common' as Rarity,
});

export function itemDef(id: string | null | undefined): ItemDef {
  return items.get(id) ?? FALLBACK;
}

export function maxStack(id: string): number {
  return itemDef(id).stack;
}

export function isTool(id: string): boolean {
  return !!itemDef(id).tool;
}

export function toolOf(id: string): ToolSpec | null {
  return itemDef(id).tool ?? null;
}
